import React from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  MenuItem,
  TextField,
} from '@material-ui/core';

const RoleChangeDialog = ({
  currentRole,
  error,
  loadingRoles,
  onClose,
  onConfirm,
  onRoleChange,
  open,
  roles,
  saving,
  selectedRoleId,
}) => {
  const currentRoleId = String(currentRole?.id ?? '');
  const selectedRole = roles.find(
    (role) => String(role.id) === selectedRoleId
  );
  const hasChanged =
    Boolean(selectedRoleId) && selectedRoleId !== currentRoleId;
  const canSubmit = hasChanged && !loadingRoles && !saving;

  return (
    <Dialog
      id="change-user-role-dialog"
      open={open}
      onClose={saving ? undefined : onClose}
      aria-labelledby="change-user-role-title"
      aria-describedby="change-user-role-description"
      className="user-role-dialog"
      fullWidth
      maxWidth="xs"
    >
      <DialogTitle id="change-user-role-title">Cambiar rol</DialogTitle>
      <DialogContent>
        <DialogContentText id="change-user-role-description">
          Rol actual: <strong>{currentRole?.name || 'Sin rol'}</strong>
        </DialogContentText>

        <TextField
          select
          fullWidth
          variant="outlined"
          label="Nuevo rol"
          value={selectedRoleId}
          onChange={(event) => onRoleChange(event.target.value)}
          disabled={loadingRoles || saving}
          helperText={
            loadingRoles
              ? 'Cargando roles disponibles...'
              : 'Selecciona Mentee, Mentor o Ninguno.'
          }
          inputProps={{
            'aria-label': 'Nuevo rol del usuario',
          }}
        >
          {roles.map((role) => (
            <MenuItem key={role.id} value={String(role.id)}>
              {role.name}
              {String(role.id) === currentRoleId ? ' (actual)' : ''}
            </MenuItem>
          ))}
        </TextField>

        <div className="user-role-dialog__warning" role="note">
          {selectedRole?.type === 'none'
            ? 'Al confirmar, el usuario podrá ingresar directamente con sus datos personales actuales.'
            : 'Al confirmar, el registro del usuario volverá a quedar incompleto. En su próximo inicio de sesión deberá completar el formulario correspondiente al nuevo rol.'}
        </div>

        {error && (
          <div className="user-role-dialog__error" role="alert">
            {error}
          </div>
        )}
      </DialogContent>
      <DialogActions>
        <Button type="button" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        <Button
          type="button"
          onClick={onConfirm}
          color="primary"
          variant="contained"
          disabled={!canSubmit}
        >
          {saving ? (
            <>
              <CircularProgress size={18} color="inherit" />
              <span className="user-role-dialog__loading-label">
                Guardando...
              </span>
            </>
          ) : (
            'Cambiar rol'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

RoleChangeDialog.propTypes = {
  currentRole: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    name: PropTypes.string,
  }),
  error: PropTypes.string,
  loadingRoles: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onRoleChange: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  roles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string.isRequired,
      type: PropTypes.string.isRequired,
    })
  ).isRequired,
  saving: PropTypes.bool.isRequired,
  selectedRoleId: PropTypes.string.isRequired,
};

RoleChangeDialog.defaultProps = {
  currentRole: null,
  error: '',
};

export default RoleChangeDialog;
